import React, { useState, useEffect } from 'react';
import { pointsAdminService, PointsStats, BusinessMetrics } from '../../services/pointsAdminService';
import styles from './FuturisticAdmin.module.css';

interface PointsAnalyticsProps {
  onError: (error: string) => void;
}

const PointsAnalytics: React.FC<PointsAnalyticsProps> = ({ onError }) => {
  const [stats, setStats] = useState<PointsStats | null>(null);
  const [businessMetrics, setBusinessMetrics] = useState<BusinessMetrics | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadAnalyticsData();
  }, []);

  const loadAnalyticsData = async () => {
    try {
      setLoading(true);
      const [statsData, metricsData] = await Promise.all([
        pointsAdminService.getSystemStats(),
        pointsAdminService.getBusinessMetrics()
      ]);
      
      setStats(statsData);
      setBusinessMetrics(metricsData);
    } catch (error) {
      onError(error instanceof Error ? error.message : 'Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  const formatNumber = (num: number): string => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toString();
  };

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        padding: '4rem',
        color: '#06b6d4' 
      }}>
        <div>Loading analytics...</div>
      </div>
    );
  }

  return (
    <div style={{ color: 'white', padding: '1.5rem' }}>
      {/* Header */}
      <div style={{ marginBottom: '2rem' }}>
        <h2 style={{ 
          fontSize: '1.5rem', 
          fontWeight: 'bold', 
          color: '#06b6d4',
          marginBottom: '0.5rem' 
        }}>
          📊 System Analytics
        </h2>
        <p style={{ color: '#9ca3af', fontSize: '0.875rem' }}>
          Real-time points system metrics and business intelligence
        </p>
      </div>

      {/* Quick Stats Grid */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
        gap: '1rem',
        marginBottom: '2rem'
      }}>
        <StatCard
          value={formatNumber(stats?.totalAllocated || 0)}
          label="Total Points Allocated"
          color="#06b6d4"
          trend={`${stats?.conversionRate || 0}% conversion`}
        />
        <StatCard
          value={formatNumber(stats?.dailyUsage || 0)}
          label="Points Used Today"
          color="#10b981"
          trend={`${stats?.activeUsers || 0} active users`}
        />
        <StatCard
          value={formatNumber(stats?.unlimitedUsers || 0)}
          label="Unlimited Users"
          color="#f59e0b"
          trend="Throttle managed"
        />
        <StatCard
          value={formatCurrency(businessMetrics?.revenue.monthly || 0)}
          label="Monthly Revenue"
          color="#8b5cf6"
          trend={`${businessMetrics?.revenue.growth || 0}% growth`}
        />
      </div>

      {/* Business Metrics */}
      {businessMetrics && (
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', 
          gap: '1.5rem' 
        }}>
          <BusinessCard
            title="💰 Revenue Metrics"
            data={[
              { label: 'Daily Revenue', value: formatCurrency(businessMetrics.revenue.daily) },
              { label: 'Monthly Revenue', value: formatCurrency(businessMetrics.revenue.monthly) },
              { label: 'Growth Rate', value: `${businessMetrics.revenue.growth}%` }
            ]}
          />
          <BusinessCard
            title="🏭 Cost Analysis"
            data={[
              { label: 'Infrastructure Cost', value: formatCurrency(businessMetrics.costs.infrastructure) },
              { label: 'Cost per Point', value: `$${businessMetrics.costs.perPoint.toFixed(4)}` },
              { label: 'Profit Margin', value: `${businessMetrics.costs.margin}%` }
            ]}
          />
        </div>
      )}
    </div>
  );
};

interface StatCardProps {
  value: string;
  label: string;
  color: string;
  trend?: string;
}

const StatCard: React.FC<StatCardProps> = ({ value, label, color, trend }) => (
  <div style={{ 
    background: 'rgba(15, 23, 42, 0.8)', 
    border: '1px solid rgba(71, 85, 105, 0.3)',
    borderRadius: '12px',
    padding: '1.5rem',
    textAlign: 'center'
  }}>
    <div style={{ 
      fontSize: '2rem', 
      fontWeight: 'bold', 
      color, 
      marginBottom: '0.5rem' 
    }}>
      {value}
    </div>
    <div style={{ fontSize: '0.875rem', color: '#e5e7eb', marginBottom: '0.25rem' }}>
      {label}
    </div>
    {trend && (
      <div style={{ fontSize: '0.75rem', color: '#9ca3af' }}>
        {trend}
      </div>
    )}
  </div>
);

interface BusinessCardProps {
  title: string;
  data: Array<{ label: string; value: string }>;
}

const BusinessCard: React.FC<BusinessCardProps> = ({ title, data }) => (
  <div style={{ 
    background: 'rgba(15, 23, 42, 0.8)', 
    border: '1px solid rgba(71, 85, 105, 0.3)',
    borderRadius: '12px',
    padding: '1.5rem'
  }}>
    <h3 style={{ color: '#06b6d4', marginBottom: '1rem', fontSize: '1.1rem' }}>
      {title}
    </h3>
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
      {data.map((item, index) => (
        <div key={index} style={{ display: 'flex', justifyContent: 'space-between' }}>
          <span style={{ color: '#9ca3af', fontSize: '0.875rem' }}>{item.label}</span>
          <span style={{ color: '#e5e7eb', fontWeight: '500' }}>{item.value}</span>
        </div>
      ))}
    </div>
  </div>
);

export default PointsAnalytics;
